import { useContext, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import { Draggable } from "@hello-pangea/dnd";
import { useMaterialUIController } from "context";
import { useGetTopologyDataQuery, useGetDataQuery } from "features/apiSlice";
import topologyData from "../../data/topologyData";
import Topology from "./Topology";
import locationTreeDataTransform from "../../utils/locationTreeDataTransform";
import nodeCount from "../../utils/nodeCount";
import ContextMenu from "../ContextMenu";
import MDTypography from "components/MDTypography";
import { NETWORK_TOPOLOGY_DROPDOWN_LIST } from "../../configs/dropdownConfigs";
import Dropdown from "../../Dropdown";
import useCenteredTree from "./Topology/Helpers/useCenteredTree";
import SeverityContext from "../../context/severityContext";

function LocationTree({
  widgetHeight,
  widget,
  cellWidgetInfo,
  index,
  masterContainerId,
  cellId,
  handleDeleteWidget,
  containerType,
  isEditMode,
  isAdmin,
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const darkModeContext = useMaterialUIController();

  const isDarkMode = darkModeContext[0].darkMode;

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const handleClose = () => setContextMenu(null);

  let getDataURL = null;
  let getCategoryDataURL = null;
  let refreshInterval = 0;
  if (widget) {
    getDataURL = widget.getDataURL;
    getCategoryDataURL = process.env.REACT_APP_CATEGORY_LIST_ENDPOINT;
    refreshInterval = widget.refreshInterval ? widget.refreshInterval : 0;
  }

  const topologyQuery = getDataURL
    ? useGetTopologyDataQuery(getDataURL, {
        pollingInterval: refreshInterval,
        refetchOnReconnect: true,
      })
    : {};

  const categoryListQuery = getCategoryDataURL
    ? useGetDataQuery(getCategoryDataURL)
    : {};

  const severityListQuery = useContext(SeverityContext);

  const draggableId = `${cellWidgetInfo.widgetType}/////${
    cellWidgetInfo.id
  }/////${uuidv4()}`;

  let primaryLabel = null;
  let secondaryLabel = null;
  let data = null;
  let descendantsData = null;
  let categoryData = null;
  let severityData = null;
  let error = null;
  if (widget) {
    if (topologyQuery.data) {
      primaryLabel = widget.primaryLabel ? widget.primaryLabel : "";
      secondaryLabel = widget.secondaryLabel ? widget.secondaryLabel : "";
      data = locationTreeDataTransform(topologyQuery.data, "locationTree");
      descendantsData = nodeCount(topologyQuery.data);
      categoryData = categoryListQuery.data ? categoryListQuery.data : [];
      severityData = severityListQuery.data ? severityListQuery.data : [];
    } else {
      primaryLabel = "Unable to update the widget";
      secondaryLabel = "";
      data = {};
      descendantsData = {};
      categoryData = [];
      severityData = [];
      error = (
        <Icon
          sx={{ marginLeft: "auto", marginTop: ".3em", color: "red" }}
          fontSize="medium"
        >
          warningambericon
        </Icon>
      );
    }
  } else {
    primaryLabel = "Dummy Primary Label";
    secondaryLabel = "Widget ID not configured";
    data = locationTreeDataTransform(topologyData, "locationTree");
    descendantsData = nodeCount(topologyData);
    categoryData = [];
    severityData = [];
    error = (
      <Icon
        sx={{ marginLeft: "auto", marginTop: ".3em", color: "red" }}
        fontSize="medium"
      >
        warningambericon
      </Icon>
    );
  }

  const [refreshLocationTree, setRefreshLocationTree] = useState(false);

  const [translate, containerRef] = useCenteredTree();

  const toggleRefreshLocationTree = () =>
    setRefreshLocationTree((prev) => !prev);

  const graphTopologyContainer = (provided = { dragHandleProps: {} }) => (
    <MDBox p={2} position="relative" height="90%" ref={containerRef}>
      <MDBox
        width="2.5rem"
        height="2.5rem"
        bgColor="info"
        variant="gradient"
        coloredShadow="info"
        borderRadius="lg"
        display="flex"
        justifyContent="center"
        alignItems="center"
        color="white"
        position="absolute"
        top="-.7em"
        {...provided.dragHandleProps}
      >
        <Icon fontSize="small">hub</Icon>
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" width="100%">
        <MDBox ml={6}>
          <MDTypography variant="h6">{primaryLabel}</MDTypography>
          <MDTypography component="div" variant="button" color="text">
            {secondaryLabel}
          </MDTypography>
        </MDBox>
        {error}
        <MDBox position="absolute" right=".9em">
          {isEditMode && isAdmin && (
            <Dropdown
              handleDeleteWidget={handleDeleteWidget}
              masterContainerId={masterContainerId}
              cellId={cellId}
              index={index}
              options={NETWORK_TOPOLOGY_DROPDOWN_LIST}
            />
          )}
        </MDBox>
      </MDBox>
      {useMemo(
        () => (
          <Topology
            data={data}
            descendantsData={descendantsData}
            categoryData={categoryData}
            severityData={severityData}
            isDarkMode={isDarkMode}
            translate={translate}
            toggleRefreshLocationTree={toggleRefreshLocationTree}
            isEditMode={isEditMode}
            isAdmin={isAdmin}
          />
        ),
        [
          topologyQuery.data,
          translate,
          refreshLocationTree,
          categoryListQuery.data,
          severityListQuery.data,
        ],
      )}
    </MDBox>
  );

  const pdfExportId = `${primaryLabel}/${cellWidgetInfo.id}`;

  return isEditMode ? (
    <Draggable draggableId={draggableId} index={index}>
      {(provided) => (
        <Card
          sx={{
            flexGrow: "1",
            flexBasis: "0",
            margin: ".5em",
            marginTop:
              index > 0 && containerType === "vertical" ? "1em" : "0.5em",
            height:
              Object.keys(data).length > 0
                ? parseInt(widgetHeight, 10) * 12
                : parseInt(widgetHeight, 10) * 4,
          }}
          {...provided.draggableProps}
          ref={provided.innerRef}
        >
          {graphTopologyContainer(provided)}
        </Card>
      )}
    </Draggable>
  ) : (
    <Card
      sx={{
        flexGrow: "1",
        flexBasis: "0",
        margin: ".5em",
        marginTop: index > 0 && containerType === "vertical" ? "1em" : "0.5em",
        height:
          Object.keys(data).length > 0
            ? parseInt(widgetHeight, 10) * 12
            : parseInt(widgetHeight, 10) * 4,
        cursor: "context-menu",
      }}
      onContextMenu={handleContextMenu}
      id={pdfExportId}
    >
      {graphTopologyContainer()}
      {widget && (
        <ContextMenu
          id={pdfExportId}
          contextMenu={contextMenu}
          handleClose={handleClose}
          name={primaryLabel}
        />
      )}
    </Card>
  );
}

export default LocationTree;
